import { ConsentCategory, ProviderKey, categoryOf } from './cookieCatalogue';

export interface StoredConsent {
  consentId: string;
  version: number;
  categories: ConsentCategory[];
  providers: ProviderKey[];
  timestamp: number;
}

const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const storageKey = (eventId: number) => `jirango_consent_${eventId}`;

export const readConsent = (eventId: number): StoredConsent | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(eventId));
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || typeof c.consentId !== 'string' || !Array.isArray(c.categories) || typeof c.timestamp !== 'number') return null;
    return { ...c, providers: Array.isArray(c.providers) ? c.providers : [] };
  } catch (e) {
    return null;
  }
};

export const writeConsent = (eventId: number, consent: StoredConsent) => {
  try {
    window.localStorage.setItem(storageKey(eventId), JSON.stringify(consent));
  } catch (e) {
    // Storage blocked (private mode etc.): the choice lasts for this page view only.
  }
};

/** True when the visitor must be asked (again). */
export const needsPrompt = (c: StoredConsent | null, version: number, providersInUse: ProviderKey[]): boolean => {
  if (!c) return true;
  if (Date.now() - c.timestamp > MAX_AGE_MS) return true;
  if (c.version !== version) return true;
  return providersInUse.some(p => {
    const cat = categoryOf(p);
    return cat !== 'necessary' && c.categories.includes(cat) && !c.providers.includes(p);
  });
};

export const newConsentId = (): string => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const TRACKING_COOKIES: { category: ConsentCategory; pattern: RegExp }[] = [
  { category: 'statistics', pattern: /^(_ga|_gid|_gat)/ },
  { category: 'marketing', pattern: /^(_fbp|_fbc)/ },
];

/**
 * Removes our first-party GA/Pixel cookies for categories not in `allowed`.
 * Runs on save and again on every page load, because gtag rewrites its cookie while the page unloads.
 * Third-party cookies (youtube.com etc.) cannot be reached.
 */
export const deleteTrackingCookies = (allowed: ConsentCategory[]) => {
  const blocked = TRACKING_COOKIES.filter(t => !allowed.includes(t.category)).map(t => t.pattern);
  if (blocked.length === 0) return;
  const names = document.cookie
    .split(';')
    .map(c => c.split('=')[0].trim())
    .filter(n => blocked.some(p => p.test(n)));
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = ['', host, `.${host}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);
  for (const name of names) {
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    }
  }
};
